/* Deep-surface mount roots (Package 4 — locked-contract fix: NO fixed-ID DOM
 * relocation). The legacy IMPACT search (views/search.ts + timeline/trend/
 * smallMultiples) and AIQ (views/ai.ts) renderers target fixed ids (#serpBody /
 * #aiBody and their sub-ids #tlGrid, #smGrid, #peerBars, …). To render the SAME
 * renderer into a SECOND mount (the deep-surface Search/AI pages) without moving
 * any node, each renderer records the root it's currently painting into here,
 * and its sub-renderers scope their id lookups to that root via
 * `serpRoot().querySelector('#x')` instead of `document.getElementById('x')`.
 *
 * Default `document`: `document.querySelector('#x')` is identical to
 * `getElementById('x')`, so the legacy sections behave EXACTLY as before. The
 * root is set at the start of every renderSearch/renderAI call (keyed per render,
 * "reset on target change"), and route entry re-renders the visible surface into
 * its own mount — so the two mounts never contend for a lookup: only the
 * just-rendered, visible surface is ever the active root. */
// Null until a render sets it; serpRoot()/aiRoot() resolve to `document` lazily
// so this module never touches `document` at eval time (node-env unit tests
// import the renderers without a DOM).
let SERP_ROOT: ParentNode | null = null;
let AI_ROOT: ParentNode | null = null;

// Monotonic render generations (FIX-audit F2). Every setSerpRoot/setAiRoot —
// i.e. every (re)render of a surface into a mount — bumps the generation, so an
// async op that captured an earlier generation can detect that a newer render
// (a different client, a different surface, or a re-render of the same one) has
// superseded it and DISCARD its commit instead of cross-painting.
let SERP_GEN = 0;
let AI_GEN = 0;

export function serpRoot(): ParentNode { return SERP_ROOT ?? document; }
export function setSerpRoot(r: ParentNode): void { SERP_ROOT = r; SERP_GEN++; }
export function aiRoot(): ParentNode { return AI_ROOT ?? document; }
export function setAiRoot(r: ParentNode): void { AI_ROOT = r; AI_GEN++; }

/** An immutable snapshot of the active SERP render context: the target root and
 * the generation it was captured at. Take one at request START, carry it across
 * every await, and gate the commit on isSerpCurrent(). */
export interface SerpContext { readonly root: ParentNode; readonly gen: number; }

function connected(r: ParentNode): boolean {
  // `document` is always connected; a detached mount root is not.
  return r === document || (r as unknown as Node).isConnected !== false;
}

/** Capture the current SERP render context (root + generation) at request start. */
export function captureSerp(): SerpContext { return { root: serpRoot(), gen: SERP_GEN }; }

/** True iff the captured context is STILL the active, attached SERP root — no
 * newer setSerpRoot() has happened and the root is still in the document. A
 * stale context (superseded surface/client, or a detached mount) returns false,
 * so its late async commit is discarded rather than painting into another
 * surface's now-current root. */
export function isSerpCurrent(ctx: SerpContext): boolean {
  return ctx.gen === SERP_GEN && ctx.root === serpRoot() && connected(ctx.root);
}
